import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  CircularProgress,  
  IconButton,  
  Menu,  
  MenuItem,  
  Switch,  
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../../components/hooks/useTablePaginationInit';
import useTableSortInit from '../../../../../../components/hooks/useTableSortInit';
import TableTimeStamps from '../../../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../../../components/utils/Copyable';
import TopBar from '../../../../../../components/utils/TopBar';
import NA from '../../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { 
  GetCCCurrenciesBIZcoreCC,
  GetPaymentCurrenciesBIZcoreCC,
  UpdatePaymentCurrencyBIZcoreCC
} from '../../../../../../redux/actions/business/core/currencyCloudActions';

import PaymentCurrenciesModal from './PaymentCurrenciesModal';

import global from "../../../../../../global.scss";

const PaymentCurrenciesPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.cc"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [paymentCurrenciesModalOpen, setPaymentCurrenciesModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [paymentCurrenciesList, setPaymentCurrenciesList] = useState([]);
  const [paymentCurrenciesTotal, setPaymentCurrenciesTotal] = useState(0);

  const [paymentCurrenciesMenuOpen, setPaymentCurrenciesMenuOpen] = useState(false);
  const [paymentCurrenciesMenuAnchor, setPaymentCurrenciesMenuAnchor] = useState(null);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const [
    sort,
    sortDirection,
    handleSort
  ] = useTableSortInit();

  // searching from a later page would land on an empty result set
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    handleChangePage(null, 0);
  };

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "Currency",
      sort: "ref_currency_id",
    },
    {
      label: "Active",
      sort: "is_active",
    },
    {
      label: "Time stamps",
      sort: "created_at",
    },
    {
      label: "",
      sort: null
    }
  ];

  useEffect(() => {
    dispatch(GetCCCurrenciesBIZcoreCC(1, 500, "", setLoading));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleGetPaymentCurrencies();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      formatPaymentCurrenciesList(state.paymentCurrenciesList.records || []);
      setPaymentCurrenciesTotal(state.paymentCurrenciesList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatPaymentCurrenciesList = (list) => {
    setPaymentCurrenciesList(list)
  };

  async function handleGetPaymentCurrencies(customLoading){
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    await dispatch(GetPaymentCurrenciesBIZcoreCC(page+1, rowsPerPage, query, customLoading ? customLoading : setLoading));
  };

  const handleTogglePaymentCurrency = (id, bool) => {
    let obj = {
      is_active: bool
    };
    dispatch(UpdatePaymentCurrencyBIZcoreCC(id, obj, setLoading)).then(() => {
      handleGetPaymentCurrencies()
    })
  };

  return (
    <Box>

      <PaymentCurrenciesModal 
        open={paymentCurrenciesModalOpen}
        setOpen={setPaymentCurrenciesModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetPaymentCurrencies}
        />

      <TopBar
        title={"Payment Currencies"}
        search={search}
        searchPlaceholder={"Search id or currency"}
        onSearchChange={handleSearchChange}
        button1Text={"Add Payment Currency"}
        button1OnClick={() => {
          setUpdate({
            defaultMode: 1
          });
          setModalTitle("Add Payment Currency");
          setPaymentCurrenciesModalOpen(!paymentCurrenciesModalOpen);
        }}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          paymentCurrenciesList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No payment currency found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                paymentCurrenciesList.map((row, index) => (
                  <TableRow 
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      ({row?.ref_currency_id?.code})&nbsp;{row?.ref_currency_id?.name}
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row?.ref_currency_id?.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={row.is_active}
                        onChange={(e) => {
                          handleTogglePaymentCurrency(row.id, e.target.checked);
                        }}
                        />
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.created_at}
                        updatedAt={row.updated_at}
                        />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          setPaymentCurrenciesMenuAnchor(e.currentTarget);
                          setPaymentCurrenciesMenuOpen(true);
                          setUpdate(row);
                          setModalTitle(row.name || "");
                        }}>
                        <MoreVertIcon sx={{ color: global['secondary_light']}}/>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={paymentCurrenciesTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
      <Menu
        open={paymentCurrenciesMenuOpen}
        anchorEl={paymentCurrenciesMenuAnchor}
        onClose={() => setPaymentCurrenciesMenuOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit " + modalTitle);
            setUpdate({
              ...update,
              defaultMode: 2
            });
            setPaymentCurrenciesMenuOpen(false);
            setPaymentCurrenciesModalOpen(!paymentCurrenciesModalOpen);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default PaymentCurrenciesPage;